import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, Zap, FileText, X, Paperclip, Upload, ExternalLink, Pencil, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  listJournals, createJournal, updateJournal, deleteJournal, listAccounts, listBranches,
  uploadFile, attachmentUrl,
} from '../lib/api';
import { AccountCombobox } from '../components/AccountCombobox';
import { JURNAL_TEMPLATES } from '../lib/journalTemplates';
import { formatRp, parseNumber, formatThousands } from '../lib/format';

const todayIso = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({ account_id: '', debit: '', credit: '', memo: '' });
const emptyForm = () => ({
  date: todayIso(),
  description: '',
  branch_id: '',
  lines: [emptyLine(), emptyLine()],
  attachment_url: '',
  attachment_name: '',
});

export default function JournalPage() {
  const [journals, setJournals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [detailJournal, setDetailJournal] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [start, setStart] = useState(() => {
    // Default: awal bulan lalu → biar mencakup semua jurnal bulan berjalan & bulan lalu
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(() => {
    // Default: akhir bulan berjalan → biar penyusutan yg ditanggal akhir bulan ikut muncul
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    d.setDate(0);
    return d.toISOString().slice(0, 10);
  });
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');

  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  const formatDateShort = (iso) => {
    if (!iso || iso.length < 10) return '—';
    const [y, m, d] = iso.split('-');
    return `${parseInt(d, 10)} ${MONTHS_SHORT[parseInt(m, 10) - 1]} ${y}`;
  };

  const filteredJournals = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return journals;
    return journals.filter((j) => {
      if ((j.description || '').toLowerCase().includes(q)) return true;
      if ((j.ref || '').toLowerCase().includes(q)) return true;
      return (j.lines || []).some(
        (l) => (l.account_name || '').toLowerCase().includes(q)
          || (l.account_code || '').toLowerCase().includes(q),
      );
    });
  }, [journals, search]);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (sourceFilter !== 'all') params.source = sourceFilter;
      const [j, a, b] = await Promise.all([listJournals(params), listAccounts({ is_active: true }), listBranches()]);
      setJournals(j);
      setAccounts(a);
      setBranches(b);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [start, end, sourceFilter]);

  // Cmd/Ctrl + S untuk simpan jurnal ketika dialog terbuka
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form, editingId]);

  const totals = useMemo(() => {
    const acc = { debit: 0, credit: 0, count: journals.length };
    form.lines.forEach(() => {});
    journals.forEach((j) => { acc.debit += j.total_debit || 0; acc.credit += j.total_credit || 0; });
    return acc;
  }, [journals]);

  const formTotals = useMemo(() => {
    let d = 0, c = 0;
    form.lines.forEach((l) => { d += parseNumber(l.debit); c += parseNumber(l.credit); });
    return { debit: d, credit: c, diff: d - c, balanced: Math.abs(d - c) < 0.005 && d > 0 };
  }, [form]);

  const setLine = (idx, patch) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], ...patch };
    setForm({ ...form, lines });
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });
  const removeLine = (idx) => {
    if (form.lines.length <= 2) return;
    setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    if (!form.date) return toast.error('Tanggal wajib');
    if (!formTotals.balanced) return toast.error('Debit dan Kredit belum balance');
    const payload = {
      date: form.date,
      description: form.description,
      branch_id: form.branch_id || '',
      attachment_url: form.attachment_url || '',
      attachment_name: form.attachment_name || '',
      lines: form.lines
        .filter((l) => l.account_id && (parseNumber(l.debit) > 0 || parseNumber(l.credit) > 0))
        .map((l) => ({
          account_id: l.account_id,
          debit: parseNumber(l.debit),
          credit: parseNumber(l.credit),
          memo: l.memo || '',
        })),
    };
    try {
      if (editingId) {
        await updateJournal(editingId, payload);
        toast.success('Jurnal diperbarui');
      } else {
        await createJournal(payload);
        toast.success('Jurnal disimpan');
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal simpan');
    }
  };

  const openEdit = (j) => {
    if (j.source === 'auto_kas_harian') {
      toast.error('Jurnal otomatis tidak bisa diubah');
      return;
    }
    setEditingId(j.id);
    setForm({
      date: j.date,
      description: j.description || '',
      branch_id: j.branch_id || '',
      attachment_url: j.attachment_url || '',
      attachment_name: j.attachment_name || '',
      lines: (j.lines || []).map((l) => ({
        account_id: l.account_id,
        debit: l.debit ? new Intl.NumberFormat('id-ID').format(l.debit) : '',
        credit: l.credit ? new Intl.NumberFormat('id-ID').format(l.credit) : '',
        memo: l.memo || '',
      })),
    });
    setOpen(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const toggleExpand = (id) => {
    setExpandedId((cur) => (cur === id ? null : id));
  };

  const applyTemplate = (templateId) => {
    const t = JURNAL_TEMPLATES.find((x) => x.id === templateId);
    if (!t) return;
    const missing = [];
    const newLines = t.lines.map((l) => {
      const acc = accounts.find((a) => a.code === l.code);
      if (!acc) missing.push(l.code);
      return {
        account_id: acc?.id || '',
        debit: '',
        credit: '',
        memo: l.memo || '',
      };
    });
    setForm((f) => ({
      ...f,
      description: f.description || t.description || t.name,
      lines: newLines,
    }));
    if (missing.length) {
      toast.warning(`Akun tidak ditemukan: ${missing.join(', ')} — silakan buat di Bagan Akun`);
    } else {
      toast.success(`Template "${t.name}" diterapkan — isi nominalnya`);
    }
  };

  const remove = async (j) => {
    if (j.source === 'auto_kas_harian') {
      toast.error('Jurnal otomatis tidak bisa dihapus manual');
      return;
    }
    if (!window.confirm(`Hapus jurnal ${j.ref}?`)) return;
    try {
      await deleteJournal(j.id);
      toast.success('Jurnal dihapus');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal hapus');
    }
  };

  const onFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 10 MB');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const r = await uploadFile(file);
      setForm((f) => ({ ...f, attachment_url: r.url, attachment_name: r.original_name }));
      toast.success('Bukti berhasil diunggah');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal unggah');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const clearAttachment = () => {
    setForm((f) => ({ ...f, attachment_url: '', attachment_name: '' }));
  };

  return (
    <AccountingLayout>
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">
              Jurnal Umum
            </h1>
            <p className="text-sm text-stone-500 mt-2">
              Catat transaksi dengan entri debit dan kredit yang seimbang
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-5 rounded-lg gap-2"
            data-testid="add-journal-btn"
          >
            <Plus className="w-4 h-4" /> Buat Jurnal
          </Button>
        </div>

        {/* Search bar */}
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari deskripsi, referensi, akun…"
            data-testid="journal-search"
            className="pl-10 h-11 bg-white border-stone-200 rounded-lg text-sm"
          />
        </div>

        {/* Advanced filters (collapsed) */}
        <details className="mb-3 group" data-testid="journal-filters">
          <summary className="cursor-pointer text-xs text-stone-500 hover:text-stone-700 select-none inline-flex items-center gap-1.5">
            <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
            Filter tanggal &amp; sumber
          </summary>
          <div className="mt-3 rounded-lg border border-stone-200 bg-white p-3 flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Dari</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white" data-testid="journal-start" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Sampai</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white" data-testid="journal-end" />
            </div>
            <div className="min-w-[200px]">
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Sumber</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="bg-white" data-testid="journal-source-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="auto_kas_harian">Auto (Kas Harian)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto text-xs text-stone-500">
              {totals.count} jurnal · Debit {formatRp(totals.debit)} · Kredit {formatRp(totals.credit)}
            </div>
          </div>
        </details>

        <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="journals-table">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50/60">
                <th className="w-10"></th>
                <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 whitespace-nowrap">Tanggal</th>
                <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 whitespace-nowrap">Referensi</th>
                <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3">Deskripsi</th>
                <th className="text-right text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 whitespace-nowrap">Total</th>
                <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 whitespace-nowrap">Lampiran</th>
                <th className="text-right text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-3 whitespace-nowrap">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredJournals.map((j) => {
                const isExpanded = expandedId === j.id;
                return (
                  <React.Fragment key={j.id}>
                    <tr
                      data-testid={`journal-row-${j.ref}`}
                      onClick={() => toggleExpand(j.id)}
                      className={`cursor-pointer transition-colors border-b border-stone-100 ${isExpanded ? 'bg-emerald-50/40' : 'hover:bg-stone-50/60'}`}
                    >
                      <td className="pl-4 py-4">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpand(j.id); }}
                          className="p-1 rounded hover:bg-stone-100 text-stone-500"
                          aria-label="Toggle detail"
                        >
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-4 font-mono text-[13px] text-stone-800 whitespace-nowrap">
                        {formatDateShort(j.date)}
                      </td>
                      <td className="px-4 py-4 font-mono text-[13px] text-stone-500 whitespace-nowrap">
                        {j.ref}
                      </td>
                      <td className="px-4 py-4 text-stone-900 font-medium">
                        {j.description || <span className="text-stone-400">—</span>}
                      </td>
                      <td className="px-4 py-4 font-mono font-semibold text-stone-900 text-right whitespace-nowrap">
                        {formatRp(j.total_debit)}
                      </td>
                      <td className="px-4 py-4">
                        {j.attachment_url ? (
                          <a
                            href={attachmentUrl(j.attachment_url)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-900 text-xs"
                            title={j.attachment_name}
                            data-testid={`journal-attachment-link-${j.ref}`}
                          >
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="max-w-[120px] truncate">{j.attachment_name || 'Bukti'}</span>
                          </a>
                        ) : (
                          <span className="text-stone-300 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                          {j.source !== 'auto_kas_harian' && (
                            <>
                              <button
                                onClick={() => openEdit(j)}
                                className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
                                data-testid={`edit-journal-${j.ref}`}
                                title="Ubah"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => remove(j)}
                                className="p-1.5 rounded hover:bg-rose-50 text-rose-500"
                                data-testid={`delete-journal-${j.ref}`}
                                title="Hapus"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {j.source === 'auto_kas_harian' && (
                            <span className="text-[10px] uppercase tracking-widest text-stone-400 px-2">Auto</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr data-testid={`journal-expand-${j.ref}`} className="bg-stone-50/40 border-b border-stone-100">
                        <td colSpan={7} className="p-0">
                          <div className="px-14 pr-8 py-4">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-stone-200">
                                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide py-2">Akun</th>
                                  <th className="text-right text-xs font-semibold text-stone-500 uppercase tracking-wide py-2 w-40">Debit</th>
                                  <th className="text-right text-xs font-semibold text-stone-500 uppercase tracking-wide py-2 w-40">Kredit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(j.lines || []).map((l, i) => (
                                  <tr key={i} className="border-b border-stone-100 last:border-0">
                                    <td className="py-2">
                                      <span className="font-mono text-xs text-stone-400 mr-3">{l.account_code}</span>
                                      <span className="text-stone-800">{l.account_name}</span>
                                      {l.memo ? <span className="text-xs text-stone-400 ml-2">· {l.memo}</span> : null}
                                    </td>
                                    <td className="py-2 text-right font-mono text-stone-800">
                                      {l.debit > 0 ? formatRp(l.debit) : <span className="text-stone-300">-</span>}
                                    </td>
                                    <td className="py-2 text-right font-mono text-stone-800">
                                      {l.credit > 0 ? formatRp(l.credit) : <span className="text-stone-300">-</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {j.created_by && (
                              <div className="mt-2 text-[11px] text-stone-400">
                                Dibuat oleh <span className="font-semibold text-stone-500">{j.created_by}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filteredJournals.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center text-stone-500 py-12">
                  {search ? `Tidak ada jurnal yang cocok dengan "${search}".` : 'Belum ada jurnal.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="journal-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Ubah Jurnal' : 'Jurnal Baru'}</DialogTitle>
            <DialogDescription>
              Total Debit harus sama dengan Total Kredit sebelum bisa disimpan.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal *</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="max-w-xs"
              data-testid="journal-form-date" />
          </div>

          {!editingId && (
            <div className="border border-dashed border-emerald-200 bg-emerald-50/40 rounded-lg p-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">
                    Template Cepat
                  </span>
                </div>
                <Select value="" onValueChange={(v) => applyTemplate(v)}>
                  <SelectTrigger className="bg-white h-8 w-64" data-testid="journal-template-select">
                    <SelectValue placeholder="Pilih template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {JURNAL_TEMPLATES.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="font-medium">{t.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-stone-500">
                  Preset akan mengisi baris jurnal — Anda hanya perlu isi nominal.
                </span>
              </div>
            </div>
          )}
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Deskripsi</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Contoh: Pembayaran sewa toko bulan Agustus" rows={2}
              data-testid="journal-form-description" />
          </div>

          <div className="border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100">
                <tr>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-stone-600">Akun</th>
                  <th className="text-right px-3 py-2 text-[11px] uppercase tracking-widest text-stone-600 w-48">Debit</th>
                  <th className="text-right px-3 py-2 text-[11px] uppercase tracking-widest text-stone-600 w-48">Kredit</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => (
                  <tr key={i} className="border-t border-stone-200">
                    <td className="p-2">
                      <AccountCombobox
                        value={l.account_id}
                        onChange={(v) => setLine(i, { account_id: v })}
                        accounts={accounts}
                        testId={`journal-line-account-${i}`}
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        value={l.debit}
                        onChange={(e) => {
                          const v = formatThousands(e.target.value);
                          setLine(i, { debit: v, credit: v ? '' : l.credit });
                        }}
                        className="text-right font-mono bg-white h-9" placeholder="0"
                        data-testid={`journal-line-debit-${i}`}
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        value={l.credit}
                        onChange={(e) => {
                          const v = formatThousands(e.target.value);
                          setLine(i, { credit: v, debit: v ? '' : l.debit });
                        }}
                        className="text-right font-mono bg-white h-9" placeholder="0"
                        data-testid={`journal-line-credit-${i}`}
                      />
                    </td>
                    <td className="p-2 text-center">
                      {form.lines.length > 2 && (
                        <button onClick={() => removeLine(i)} className="text-stone-400 hover:text-red-600" title="Hapus baris">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-stone-50 border-t-2 border-stone-300">
                <tr>
                  <td className="p-2">
                    <Button variant="outline" size="sm" onClick={addLine} data-testid="journal-add-line-btn">
                      <Plus className="w-3 h-3 mr-1" /> Tambah Baris
                    </Button>
                  </td>
                  <td className="text-right px-3 py-2 num font-semibold text-stone-900">
                    {formatRp(formTotals.debit)}
                  </td>
                  <td className="text-right px-3 py-2 num font-semibold text-stone-900">
                    {formatRp(formTotals.credit)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {formTotals.balanced ? (
                      <span className="text-emerald-700 text-xs font-semibold" title="Balance">✓</span>
                    ) : (
                      <span className="text-red-700 text-xs font-semibold" data-testid="journal-form-imbalance"
                        title={`Selisih ${formatRp(Math.abs(formTotals.diff))}`}>
                        !
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="border border-stone-200 rounded-lg p-3 bg-stone-50/50 flex items-center gap-3" data-testid="journal-attachment-row">
            <div className="flex items-center gap-2 text-stone-700">
              <Paperclip className="w-4 h-4 text-emerald-800" />
              <span className="text-[11px] uppercase tracking-widest text-stone-600 font-semibold">
                Bukti Pembayaran / Invoice
              </span>
            </div>
            {form.attachment_url ? (
              <div className="flex items-center gap-2 flex-1">
                <a
                  href={attachmentUrl(form.attachment_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-emerald-800 hover:underline truncate max-w-[400px]"
                  data-testid="journal-attachment-preview"
                >
                  {form.attachment_name || 'Lihat file'}
                </a>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearAttachment}
                  className="border-red-200 text-red-700 hover:bg-red-50 h-8"
                  data-testid="journal-attachment-remove"
                >
                  <X className="w-3 h-3 mr-1" /> Hapus
                </Button>
              </div>
            ) : (
              <div className="flex-1 flex items-center gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
                    onChange={onFileSelect}
                    className="hidden"
                    data-testid="journal-attachment-input"
                    disabled={uploading}
                  />
                  <span className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border ${uploading ? 'bg-stone-100 text-stone-400 border-stone-200' : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'}`}>
                    <Upload className="w-3.5 h-3.5" />
                    {uploading ? 'Mengunggah…' : 'Pilih File'}
                  </span>
                </label>
                <span className="text-[11px] text-stone-500">
                  PDF / JPG / PNG · maks 10 MB
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditingId(null); }}>Batal</Button>
            <Button
              onClick={save}
              disabled={!formTotals.balanced}
              className="bg-emerald-800 hover:bg-emerald-900 text-white disabled:opacity-50"
              data-testid="journal-form-save"
            >
              <Save className="w-4 h-4 mr-1" /> {editingId ? 'Perbarui Jurnal' : 'Simpan Jurnal'}
              <span className="ml-2 hidden sm:inline text-[10px] font-normal opacity-70">⌘S</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailJournal} onOpenChange={(v) => !v && setDetailJournal(null)}>
        <DialogContent className="max-w-2xl" data-testid="journal-detail-dialog">
          {detailJournal && (
            <>
              <DialogHeader>
                <DialogTitle>{detailJournal.ref} · {detailJournal.date}</DialogTitle>
                <DialogDescription>
                  {detailJournal.description || '—'}
                  {detailJournal.branch_name && <> · {detailJournal.branch_name}</>}
                </DialogDescription>
              </DialogHeader>
              <table className="w-full text-sm border border-stone-200 rounded-lg overflow-hidden">
                <thead className="bg-stone-100">
                  <tr>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-stone-600">Akun</th>
                    <th className="text-right px-3 py-2 text-[11px] uppercase tracking-widest text-stone-600 w-28">Debit</th>
                    <th className="text-right px-3 py-2 text-[11px] uppercase tracking-widest text-stone-600 w-28">Kredit</th>
                  </tr>
                </thead>
                <tbody>
                  {detailJournal.lines.map((l, i) => (
                    <tr key={i} className="border-t border-stone-200">
                      <td className="p-2">
                        <div className="font-mono text-xs text-stone-500">{l.account_code}</div>
                        <div>{l.account_name}</div>
                        {l.memo && <div className="text-[11px] text-stone-500">{l.memo}</div>}
                      </td>
                      <td className="text-right px-3 py-2 num">{l.debit > 0 ? formatRp(l.debit) : ''}</td>
                      <td className="text-right px-3 py-2 num">{l.credit > 0 ? formatRp(l.credit) : ''}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-stone-50 border-t-2 border-stone-300">
                  <tr>
                    <td className="px-3 py-2 font-semibold">Total</td>
                    <td className="text-right px-3 py-2 num font-semibold">{formatRp(detailJournal.total_debit)}</td>
                    <td className="text-right px-3 py-2 num font-semibold">{formatRp(detailJournal.total_credit)}</td>
                  </tr>
                </tfoot>
              </table>
              <div className="text-xs text-stone-500 flex items-center justify-between">
                <span>Sumber: {detailJournal.source === 'auto_kas_harian' ? 'Otomatis dari Kas Harian' : 'Manual'}</span>
                <span>Dibuat: {detailJournal.created_by}</span>
              </div>
              {detailJournal.attachment_url && (
                <a
                  href={attachmentUrl(detailJournal.attachment_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-emerald-800 hover:underline mt-1"
                  data-testid="journal-detail-attachment"
                >
                  <Paperclip className="w-4 h-4" />
                  {detailJournal.attachment_name || 'Lihat bukti pembayaran'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </AccountingLayout>
  );
}
